/**
 * TopFonds for lbbw-am.de
 *
 * @author Florian Gutwald
 * Converted to Plain JS for Rollup
 */
import Handlebars from 'handlebars';
import _ from 'lodash';
import AMApp from '../../global/app';

export default class TopFonds {
  /**
   * TopFonds constructor
   *
   * @param {HTMLElement} element - The topfonds container element
   * @param {Object} options - Configuration options
   */
  constructor(element, options = {}) {
    this.element = element;
    this.amApp = new AMApp();

    this.settings = Object.assign({
      pathTopFondsFilter: AMApp.langGetParam + 'eID=fonds&function=topfondsCategories',
      pathTopFondsData: AMApp.langGetParam + 'eID=fonds&function=topfondsnew&category='
    }, options);

    this.topFonds = [];
    this.currentFilter = 'position';
    this.maxTopFonds = 6;

    // DOM Elements
    this.listContainer = null;
    this.filter = null;
    this.moreButton = null;
    this.lessButton = null;
    this.filterText = null;

    this.init();
  }

  /**
   * Initialize
   */
  init() {
    this.cacheDOM();
    this.attachEventListeners();
    this.getTopFondsFilter();
  }

  /**
   * Cache DOM elements
   */
  cacheDOM() {
    this.listContainer = document.getElementById('topfonds_list_container');
    this.filter = document.getElementById('topfonds_filter');
    this.moreButton = document.getElementById('topfonds_more');
    this.lessButton = document.getElementById('topfonds_less');
    this.filterText = document.getElementById('topfonds_filter_text');
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    // More button
    if (this.moreButton) {
      this.moreButton.addEventListener('click', () => {
        this.renderTopFonds(0, this.maxTopFonds, this.currentFilter);
        this.setStatusLess();
      });
    }

    // Less button
    if (this.lessButton) {
      this.lessButton.addEventListener('click', () => {
        this.renderTopFonds(0, 3, this.currentFilter);

        // Smooth scroll to topfonds
        const topfondsElement = document.getElementById('topfonds');
        if (topfondsElement) {
          topfondsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }

    // Filter change
    if (this.filter) {
      this.filter.addEventListener('change', (e) => {
        this.getTopFonds(e.target.value);
      });
    }
  }

  /**
   * Get topfonds filter categories
   */
  async getTopFondsFilter() {
    try {
      const response = await fetch(AMApp.basePath + this.settings.pathTopFondsFilter);
      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      this.populateTopFondsFilter(data);
    } catch (error) {
      console.error('Error fetching topfonds filter:', error);
    }
  }

  /**
   * Populate filter dropdown
   * Use key if value is empty
   *
   * @param {Object} data
   */
  populateTopFondsFilter(data) {
    if (!this.filter) return;

    let filterValues = '';
    const filter = [];
    let recommendation = null;

    // Separate recommendation from other categories
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'TopEmpfehlungen') {
        recommendation = {
          label: value,
          value: key
        };
      } else {
        filter.push({
          label: value,
          value: key
        });
      }
    });

    // Add recommendation at the beginning
    if (recommendation) {
      filter.unshift(recommendation);
    }

    // Build options HTML
    filter.forEach(item => {
      filterValues += `<option value="${item.value}">${item.label}</option>\n`;
    });

    this.filter.insertAdjacentHTML('beforeend', filterValues);

    // Load initial data
    this.getTopFonds();
  }

  /**
   * Get top fonds by category
   *
   * @param {string} category
   */
  async getTopFonds(category) {
    if (!category && this.filter) {
      category = this.filter.value;
    }

    const request = AMApp.basePath + this.settings.pathTopFondsData + category;

    // Show loader
    if (this.listContainer) {
      this.listContainer.insertAdjacentHTML('beforeend', AMApp.LOADING_ANIMATION);
      this.listContainer.classList.add('topfonds-list__loading');
    }

    // Hide current list items
    const listItems = document.querySelectorAll('.topfonds-list-items');
    listItems.forEach(item => item.style.display = 'none');

    try {
      const response = await fetch(request);
      if (!response.ok) throw new Error('Network response was not ok');

      const result = await response.json();

      if (result && result.length > 0) {
        this.topFonds = result;
        const stop = Math.min(3, this.topFonds.length);
        this.renderTopFonds(0, stop);
      } else {
        this.showTopFondsStatus('error');
      }
    } catch (error) {
      console.error('Error fetching topfonds:', error);
      this.showTopFondsStatus('error');
    }
  }

  /**
   * Render topfonds list
   *
   * @param {number} start
   * @param {number} stop
   */
  renderTopFonds(start, stop) {
    if (!this.listContainer) return;

    this.listContainer.classList.remove('topfonds-list__loading');

    // Get visible list container
    const currentList = document.querySelector('.topfonds-list-items:not([style*="display: none"])');
    if (currentList) {
      currentList.style.display = 'block';
    }

    this.removeLoader();

    const topFondsSorted = this.sortTopFonds();
    const items = [];

    // Update button visibility
    if (topFondsSorted.length > 3) {
      this.setStatusMore();
    } else if (topFondsSorted.length <= 3) {
      this.setStatusThree();
    } else {
      this.setStatusLess();
    }

    // Prepare items for rendering
    if (topFondsSorted.length > 0) {
      for (let i = start; i < stop; i += 1) {
        const fonds = topFondsSorted[i];
        if (fonds) {
          fonds.index = i + 1;

          // Set zusatztext if empty
          if (!fonds.zusatztext_stringS || fonds.zusatztext_stringS === '') {
            fonds.zusatztext_stringS = fonds.code_stringS.replace('Top', '');
          }

          items.push(fonds);
        }
      }

      // Update filter text
      if (this.filterText && topFondsSorted[0]) {
        this.filterText.textContent = topFondsSorted[0].zusatztext_stringS;
      }
    }

    // Render with Handlebars
    const templateEl = document.getElementById('topfondslist_template');
    if (templateEl && currentList) {
      const topFondsListRaw = templateEl.innerHTML;
      const topFondsListTemplate = Handlebars.compile(
        this.amApp.toHandlebarsDelimiters(topFondsListRaw)
      );

      currentList.innerHTML = topFondsListTemplate({ items: items });
    }
  }

  /**
   * Sort topfonds by position and name
   *
   * @returns {Array}
   */
  sortTopFonds() {
    return _.orderBy(this.topFonds, ['pos_intS', 'name_stringS'], ['asc', 'asc']);
  }

  /**
   * Status: Show more button
   */
  setStatusMore() {
    if (this.moreButton) {
      this.moreButton.classList.remove('d-none');
    }
    if (this.lessButton) {
      this.lessButton.classList.add('d-none');
    }
  }

  /**
   * Status: Show less button
   */
  setStatusLess() {
    if (this.moreButton) {
      this.moreButton.classList.add('d-none');
    }
    if (this.lessButton) {
      this.lessButton.classList.remove('d-none');
    }
  }

  /**
   * Status: Hide both buttons (3 or fewer items)
   */
  setStatusThree() {
    if (this.moreButton) {
      this.moreButton.classList.add('d-none');
    }
    if (this.lessButton) {
      this.lessButton.classList.add('d-none');
    }
  }

  /**
   * Remove loader element
   */
  removeLoader() {
    if (this.listContainer) {
      const loaders = this.listContainer.querySelectorAll('.loader');
      loaders.forEach(loader => loader.remove());
    }
  }

  /**
   * Show message
   *
   * @param {HTMLElement} target
   * @param {string} message
   */
  showMessage(target, message) {
    if (target) {
      const messageEl = document.createElement('p');
      messageEl.className = 'fondsdetail-noresult';
      messageEl.textContent = message;
      target.insertAdjacentElement('beforebegin', messageEl);
    }
  }

  /**
   * Show topfonds status message
   *
   * @param {string} status
   */
  showTopFondsStatus(status) {
    this.removeLoader();

    switch (status) {
      case 'error':
        this.showMessage(
          this.listContainer,
          AMApp.translations?.common?.noService || 'Service nicht verfügbar'
        );
        break;
      default:
        break;
    }
  }

  /**
   * Destroy the TopFonds instance
   */
  destroy() {
    this.removeLoader();
    this.topFonds = [];
  }
}
